import { Card } from "@/components/Card";
import { H3, P } from "@/components/Typography";
import { getRepeatPurchaseRevenue } from "@/utils/emailDashboardMetricsHelper";
import { faCoins } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import {
  TooltipContentProps,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

interface IProps {
  data: IGetEmailDashboardRS;
}

const RepeatPurchaseRevenue = ({ data }: IProps) => {
  const { t } = useTranslation();
  const repeatPurchaseRevenueData = getRepeatPurchaseRevenue(data?.rawData);

  const basicFormatNumber = (number: number) =>
    new Intl.NumberFormat("en-US", {
      notation: "standard",
      compactDisplay: "short",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(number);

  const getChartTooltip = ({
    active,
    payload,
    label,
  }: TooltipContentProps<ValueType, NameType>) => {
    if (!active || !payload || !payload.length) return null;

    const tickPayload = payload[0];

    return (
      <div className="flex flex-col rounded border border-gray-light bg-white p-10 shadow-sm">
        <span>{label}</span>
        <span
          style={{
            color: tickPayload.color,
          }}>
          {t("email.dashboard.charts.repeatPurchases.tooltip")}: $
          {basicFormatNumber(tickPayload.value)}
        </span>
      </div>
    );
  };

  return (
    <Card shadow additionalClassName="p-20 border border-gray-light">
      <div className="flex flex-row items-center gap-10">
        <FontAwesomeIcon icon={faCoins} className="text-2xl text-primary" />
        <H3 className="font-bold">
          {t("email.dashboard.charts.repeatPurchases.title")}
        </H3>
      </div>
      <P className="mb-20">
        {t("email.dashboard.charts.repeatPurchases.description")}
      </P>
      <BarChart
        style={{
          width: "100%",
          maxWidth: "700px",
          maxHeight: "70vh",
          aspectRatio: 1.618,
        }}
        responsive
        data={repeatPurchaseRevenueData}>
        <XAxis
          dataKey="month"
          type="category"
          interval="equidistantPreserveStart"
          tick={{ fontSize: 10 }}
        />
        <YAxis
          width="auto"
          tickFormatter={value => `$${basicFormatNumber(value)}`}
          tick={{ fontSize: 10 }}
        />
        <Tooltip content={t => getChartTooltip(t)} />
        <Bar
          type="monotone"
          dataKey="netNewRepeatPurchaseRevenue"
          stroke="#4ACE96"
          fill="#4ACE96"
        />
      </BarChart>
    </Card>
  );
};

export default RepeatPurchaseRevenue;
